import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { List, Kanban, ArrowRight } from "lucide-react";

// ── 5-stage Resale MVP pipeline ───────────────────────────────────────
// The five visual stages surface the pipeline in language a media-wall
// business owner recognises. Historic statuses (reviewing, nurture,
// converted, proposal_opportunity) remain intact in the database and
// map to a visible stage; they are NOT deleted or migrated.
//
// PROJECT CREATED is a factual state — a lead only appears here when
// it has a linked converted_project_id (or the historic "converted"
// status), never based purely on a sales label. Drag/drop into that
// column is disabled: the user must use the "Convert to Project" flow
// from the lead detail page so a real project is created first.
const STAGES = [
  { key: "new",             label: "New",             primaryStatus: "new",          hint: "Fresh enquiries awaiting review" },
  { key: "qualified",       label: "Qualified",       primaryStatus: "qualified",    hint: "Confirmed fit — worth pursuing" },
  { key: "consultation",    label: "Consultation",    primaryStatus: "consultation", hint: "Site visit or design call booked" },
  { key: "project_created", label: "Project Created", primaryStatus: null,           hint: "A project record exists for this lead" },
  { key: "lost",            label: "Lost",            primaryStatus: "lost",         hint: "Not moving forward" },
];

// Historic status → visual stage mapping (UI-only, non-destructive).
const STATUS_TO_STAGE = {
  new: "new",
  reviewing: "new",
  qualified: "qualified",
  nurture: "qualified",
  proposal_opportunity: "qualified",
  consultation: "consultation",
  converted: "project_created",
  lost: "lost",
};

// Sub-badge for legacy statuses so the user can still see the underlying
// value without exposing 8 raw internal statuses as top-level columns.
const LEGACY_STATUS_LABEL = {
  reviewing: "Reviewing",
  nurture: "Nurturing",
  proposal_opportunity: "Proposal opp.",
  converted: "Converted",
};

const stageForLead = (lead) => {
  if (lead.converted_project_id || lead.status === "converted") return "project_created";
  return STATUS_TO_STAGE[lead.status] || "new";
};

const CLASS_STYLES = {
  Priority: "bg-[color:hsl(var(--accent))] text-white",
  Qualified: "bg-foreground text-background",
  Nurture: "bg-muted text-foreground",
  "Low Fit": "bg-muted text-muted-foreground",
};

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [view, setView] = useState("kanban");
  const nav = useNavigate();
  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const moveLead = async (id, targetStageKey) => {
    const stage = STAGES.find((s) => s.key === targetStageKey);
    if (!stage) return;
    if (stage.key === "project_created") {
      toast("Open the lead and use “Convert to Project”", {
        description: "A lead only enters Project Created when a real project has been created for it.",
      });
      return;
    }
    try {
      await api.patch(`/leads/${id}`, { status: stage.primaryStatus });
      toast.success("Lead updated");
      load();
    } catch (e) { toast.error("Could not update lead"); }
  };

  return (
    <div className="px-6 lg:px-14 py-10 lg:py-14 max-w-[1400px]" data-testid="leads-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline">Studio · Sales</div>
          <h1 className="font-serif font-light mt-3" style={{ fontSize: "44px", lineHeight: 1.05 }}>Leads</h1>
          <p className="text-muted-foreground mt-3">Move enquiries through five simple stages, from a first message to a live project.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="btn-view-kanban" onClick={() => setView("kanban")} className={`btn-outline ${view === "kanban" ? "bg-foreground text-background" : ""}`}>
            <Kanban className="w-4 h-4" strokeWidth={1.5}/> Kanban
          </button>
          <button data-testid="btn-view-list" onClick={() => setView("list")} className={`btn-outline ${view === "list" ? "bg-foreground text-background" : ""}`}>
            <List className="w-4 h-4" strokeWidth={1.5}/> List
          </button>
        </div>
      </div>

      {leads.length === 0 && (
        <div className="luxe-card p-16 text-center mb-6" data-testid="leads-empty-state">
          <div className="font-serif text-2xl mb-2">No leads yet</div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            New enquiries from your public site will land here automatically. As you qualify them, drag them across the stages.
          </p>
        </div>
      )}

      {view === "kanban" ? (
        <KanbanView leads={leads} onMove={moveLead} onOpen={(id) => nav(`/leads/${id}`)}/>
      ) : (
        <ListView leads={leads} onOpen={(id) => nav(`/leads/${id}`)}/>
      )}
    </div>
  );
}

function LeadCard({ lead, onOpen, onDragStart }) {
  const cls = CLASS_STYLES[lead.classification] || "bg-muted text-muted-foreground";
  const age = Math.max(1, Math.round((Date.now() - new Date(lead.created_at)) / (1000 * 60 * 60 * 24)));
  const legacyLabel = LEGACY_STATUS_LABEL[lead.status];
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={() => onOpen(lead.id)}
      data-testid={`lead-card-${lead.id}`}
      className="bg-card border border-border p-4 mb-2 cursor-pointer hover:border-foreground transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{lead.reference}</div>
          <div className="font-serif text-lg mt-1 truncate">{lead.first_name} {lead.last_name}</div>
          <div className="text-xs text-muted-foreground mt-0.5 truncate">{lead.suburb || "—"} · {lead.budget_band || "—"}</div>
        </div>
        <div className={`text-[10px] uppercase tracking-widest px-2 py-1 shrink-0 ${cls}`}>{lead.classification || "—"}</div>
      </div>
      <div className="flex justify-between items-end mt-3 pt-3 border-t border-border gap-2">
        <div className="text-xs text-muted-foreground min-w-0 truncate">
          {lead.source || "web"} · {age}d
          {legacyLabel && (
            <span className="ml-2 text-[9px] uppercase tracking-widest text-muted-foreground/80 border border-border px-1 py-px"
                  data-testid={`lead-legacy-badge-${lead.id}`}>{legacyLabel}</span>
          )}
        </div>
        <div className="font-serif text-sm shrink-0">Score {lead.score ?? "—"}</div>
      </div>
    </div>
  );
}

function KanbanView({ leads, onMove, onOpen }) {
  const [dragging, setDragging] = useState(null);
  return (
    <div className="overflow-x-auto pb-4 -mx-6 px-6">
      <div className="flex gap-4 min-w-max">
        {STAGES.map((s) => {
          const col = leads.filter((l) => stageForLead(l) === s.key);
          const isProjectCreated = s.key === "project_created";
          return (
            <div key={s.key} className="w-72 shrink-0"
                 onDragOver={(e) => { if (!isProjectCreated) e.preventDefault(); }}
                 onDrop={() => dragging && onMove(dragging.id, s.key)}
                 data-testid={`kanban-col-${s.key}`}>
              <div className="flex items-center justify-between px-2 pb-3">
                <div>
                  <div className="overline">{s.label}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{s.hint}</div>
                </div>
                <div className="text-xs text-muted-foreground">{col.length}</div>
              </div>
              <div className={`min-h-[240px] p-2 bg-[color:hsl(var(--surface-panel))] border border-border ${isProjectCreated ? "border-dashed" : ""}`}>
                {col.map((l) => (
                  <LeadCard key={l.id} lead={l} onOpen={onOpen}
                    onDragStart={() => setDragging(l)}/>
                ))}
                {col.length === 0 && (
                  <div className="text-[11px] text-muted-foreground p-4 text-center"
                       data-testid={`kanban-col-empty-${s.key}`}>
                    {isProjectCreated
                      ? "Convert a qualified lead into a project to see it here."
                      : "No leads in this stage yet."}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ListView({ leads, onOpen }) {
  return (
    <div className="luxe-card overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border" style={{ background: "#F4F2EC" }}>
            <th className="text-left px-6 py-4 overline text-[10px]">Name</th>
            <th className="text-left px-6 py-4 overline text-[10px] hidden md:table-cell">Suburb</th>
            <th className="text-left px-6 py-4 overline text-[10px] hidden md:table-cell">Budget</th>
            <th className="text-right px-6 py-4 overline text-[10px]">Score</th>
            <th className="text-right px-6 py-4 overline text-[10px]">Stage</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => {
            const stage = STAGES.find((s) => s.key === stageForLead(l));
            return (
              <tr key={l.id} className="border-b border-border last:border-0 cursor-pointer hover:bg-muted/40" onClick={() => onOpen(l.id)} data-testid={`lead-row-${l.id}`}>
                <td className="px-6 py-4">
                  <div className="font-medium">{l.first_name} {l.last_name}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{l.reference}</div>
                </td>
                <td className="px-6 py-4 hidden md:table-cell text-muted-foreground">{l.suburb}</td>
                <td className="px-6 py-4 hidden md:table-cell text-muted-foreground">{l.budget_band}</td>
                <td className="px-6 py-4 text-right">{l.score}</td>
                <td className="px-6 py-4 text-right">
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-muted">{stage?.label || l.status}</span>
                </td>
                <td className="px-6 py-4 text-muted-foreground"><ArrowRight className="w-4 h-4" strokeWidth={1.5}/></td>
              </tr>
            );
          })}
          {leads.length === 0 && (
            <tr><td colSpan="6" className="px-6 py-16 text-center" data-testid="leads-empty-list">
              <div className="font-serif text-2xl text-foreground/80">No leads yet</div>
              <div className="text-xs text-muted-foreground mt-2 max-w-md mx-auto">
                New enquiries will appear here automatically.
              </div>
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
